// AppShell — nova casca do painel: sidebar + topbar + main.
// Usa react-router-dom v6 (NavLink/Outlet).

import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ReactNode, useMemo } from "react";
import { BrandMark } from "../components/BrandMark";
import { useTheme } from "../hooks/useTheme";
import styles from "./AppShell.module.scss";

interface AppShellProps {
  // Quando usada como elemento de rota com rotas filhas, renderiza <Outlet/>.
  // Quando envolvida em volta de uma página específica (padrão de uso atual
  // via ProtectedLayout), recebe children direto.
  children?: ReactNode;
}

const NAV = [
  { to: "/dashboard", label: "Painel", icon: IconGrid },
  { to: "/brands", label: "Marcas", icon: IconLayers },
  { to: "/files", label: "Arquivos", icon: IconUpload },
  { to: "/users", label: "Pessoas", icon: IconUsers },
];

const SYSTEM_NAV = [
  { to: "/settings", label: "Ajustes", icon: IconGear },
];

const CRUMB_MAP: Record<string, string> = {
  "/dashboard": "Painel",
  "/brands": "Marcas",
  "/files": "Arquivos",
  "/users": "Pessoas",
  "/settings": "Ajustes",
};

export function AppShell({ children }: AppShellProps = {}) {
  const { toggle } = useTheme();
  const location = useLocation();

  const crumb = useMemo(() => {
    const segs = location.pathname.split("/").filter(Boolean);
    const base = "/" + (segs[0] || "dashboard");
    return CRUMB_MAP[base] || "Painel";
  }, [location.pathname]);

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <BrandMark />
        </div>
        <nav className={styles.nav}>
          <h4 className={styles.navHead}>Trabalho</h4>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ""}`}
            >
              <n.icon /> <span>{n.label}</span>
            </NavLink>
          ))}
          <h4 className={styles.navHead}>Sistema</h4>
          {SYSTEM_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ""}`}
            >
              <n.icon /> <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className={styles.right}>
        <header className={styles.topbar}>
          <nav className={styles.crumbs} aria-label="caminho">
            <span>{crumb}</span>
          </nav>

          <div className={styles.search}>
            <IconSearch />
            <input placeholder="Buscar marcas, arquivos, pessoas…" aria-label="Buscar" />
          </div>

          <button className={styles.iconBtn} onClick={toggle} aria-label="Alternar tema" title="Alternar tema">
            <IconMoon />
          </button>
        </header>

        <main className={styles.main}>
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

/* ---------- icons inline (sem lib externa) ---------- */
function IconGrid() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}
function IconLayers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}
function IconUpload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconGear() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
function IconMoon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
